"use client";
import * as React from "react";
import { ArrowRight } from "lucide-react";
import { RENEWAL_APPS, type AppId } from "../apps.config";
import { AppIcon } from "./AppIcon";
import { cn } from "../lib/cn";

/**
 * AppSwitcher — the dropdown grid of tools. Used by <RenewalTopBar>, but
 * usable on its own anywhere you want a "jump to another tool" menu.
 * Renders nothing when `open` is false.
 */
export interface AppSwitcherProps {
  current: AppId;
  open: boolean;
  /** Override navigation (e.g. Next.js router). Defaults to window.location. */
  onSelect?: (id: AppId, url: string) => void;
  allToolsUrl?: string;
  className?: string;
}

export function AppSwitcher({ current, open, onSelect, allToolsUrl = "https://tools.renewalinitiatives.org", className }: AppSwitcherProps) {
  if (!open) return null;
  const go = (id: AppId, url: string) => (onSelect ? onSelect(id, url) : window.location.assign(url));

  return (
    <div
      className={cn(
        "absolute left-0 top-[calc(100%+8px)] z-50 w-[420px] p-2.5",
        "bg-white rounded-[var(--ri-radius-card)] border border-[var(--ri-hairline)]",
        "shadow-[0_18px_50px_rgba(45,74,42,.16)]",
        className,
      )}
      role="menu"
    >
      <div className="grid grid-cols-2 gap-1">
        {RENEWAL_APPS.map((app) => {
          const active = app.id === current;
          return (
            <button
              key={app.id}
              role="menuitem"
              onClick={() => go(app.id, app.url)}
              className={cn(
                "flex items-center gap-3 p-2.5 rounded-[10px] text-left",
                "transition-colors duration-150 ease-[var(--ri-ease)]",
                active ? "bg-[rgba(45,74,42,.06)]" : "hover:bg-[rgba(45,74,42,.05)]",
              )}
            >
              <AppIcon app={app.id} size={16} radius={8} />
              <span
                className={cn(
                  "text-[13.5px] text-[var(--ri-ink)]",
                  active ? "font-bold" : "font-semibold",
                )}
              >
                {app.name}
              </span>
            </button>
          );
        })}
      </div>
      <a
        href={allToolsUrl}
        className="mt-2 flex items-center gap-2 border-t border-[var(--ri-hairline)] px-2.5 pt-2.5 pb-1 text-[13px] font-medium text-[var(--ri-sage)] hover:text-[var(--ri-rust)] transition-colors"
      >
        All tools <ArrowRight size={14} />
      </a>
    </div>
  );
}
